import { useState, useRef, useCallback, useEffect } from 'react';
import { openNavigationForStreet } from '@/utils/navigationUtils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Home, Building2, ArrowLeft, ArrowUpDown, Trash2, Eye, EyeOff, ScanLine, StickyNote, Pencil, Map as MapIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { D2DTagSelector } from './D2DTagSelector';
import { BulkUnitCreator } from './BulkUnitCreator';
import { PresenceIndicator } from './PresenceIndicator';
import type { D2DHouse, D2DCreateHouseData, D2DCreateUnitData, D2DTag } from '@/types/d2d';
import type { UserPresence } from '@/hooks/useAreaPresence';

// Constants for state persistence
const HOUSE_LIST_SCROLL_KEY = 'house-list-scroll-position';
const HOUSE_LIST_STATE_KEY = 'house-list-state';

interface D2DHouseListProps {
  houses: D2DHouse[];
  loading: boolean;
  street?: { name: string; plz: string } | null;
  onSelectHouse: (house: D2DHouse) => void;
  onSelectHouseWithTagFilter?: (house: D2DHouse, tagFilter: string) => void;
  onCreateHouse: (houseData: D2DCreateHouseData) => Promise<D2DHouse | null>;
  onCreateUnit?: (houseId: string, unitData: D2DCreateUnitData) => Promise<void>;
  onDeleteHouse?: (houseId: string) => Promise<void>;
  onBack: () => void;
  onAddHouseNote?: (houseId: string, note: string) => Promise<void>;
  onUpdateHouseNote?: (noteId: string, note: string) => Promise<void>;
  onDeleteHouseNote?: (houseId: string, noteId: string) => Promise<void>;
  isAreaInactive?: boolean;
  getUsersInHouse?: (houseId: string) => UserPresence[];
}

// SwipeableHouseCard component
interface SwipeableHouseCardProps {
  house: D2DHouse;
  onSelect: () => void;
  onSelectWithTagFilter?: (tagFilter: string) => void;
  onDelete?: () => void;
  showDelete: boolean;
  showNotesGlobally: boolean;
  houseNotesVisibility: { [houseId: string]: boolean };
  onToggleHouseNotes: (houseId: string) => void;
  houseRef?: (node: HTMLDivElement | null) => void;
  onOpenHouseNotes?: () => void;
  isAreaInactive?: boolean;
  usersInHouse?: UserPresence[];
}

const SwipeableHouseCard = ({ house, onSelect, onSelectWithTagFilter, onDelete, showDelete, showNotesGlobally, houseNotesVisibility, onToggleHouseNotes, houseRef, onOpenHouseNotes, isAreaInactive, usersInHouse = [] }: SwipeableHouseCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const currentXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isSwipingRef = useRef(false);
  const animationFrameRef = useRef<number>();

  // Check if house has more than 1 KT tagged unit
  const hasMultipleKT = house.units && house.units.filter(u => u.tags.includes('KT')).length > 1;
  
  // Check if house has hot leads
  const hasHotLeads = house.units && house.units.some(u => u.tags.includes('HOT'));
  
  // Check if house has both hot and friendly (prioritize friendly)
  const hasHotAndFriendly = house.units && house.units.some(u => u.tags.includes('HOT')) && house.units.some(u => u.tags.includes('KT'));

  const updateTransform = useCallback((x: number) => {
    if (cardRef.current && backgroundRef.current) {
      const clampedX = Math.min(0, Math.max(-80, x));
      cardRef.current.style.transform = `translateX(${clampedX}px)`;
      
      // Show delete background when swiping left
      const opacity = Math.abs(clampedX) / 80;
      backgroundRef.current.style.opacity = opacity.toString();
    }
  }, []);

  const resetPosition = useCallback(() => {
    if (cardRef.current && backgroundRef.current) {
      cardRef.current.style.transform = 'translateX(0px)';
      cardRef.current.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      backgroundRef.current.style.opacity = '0';
      
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.style.transition = '';
        }
      }, 300);
    }
  }, []);

  const handleStart = useCallback((clientX: number, clientY: number) => {
    if (!showDelete) return;
    
    startXRef.current = clientX;
    startYRef.current = clientY;
    currentXRef.current = 0;
    isDraggingRef.current = true;
    isSwipingRef.current = false;
  }, [showDelete]);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isDraggingRef.current || !showDelete) return;
    
    const deltaX = clientX - startXRef.current;
    const deltaY = clientY - startYRef.current;
    
    // Only start swiping if movement is primarily horizontal and to the left
    if (!isSwipingRef.current) {
      const minSwipeDistance = 15; // Minimum distance before considering it a swipe
      const maxVerticalDeviation = 30; // Maximum vertical movement allowed
      
      // Check if movement is significant enough and primarily horizontal
      if (Math.abs(deltaX) < minSwipeDistance) return;
      if (Math.abs(deltaY) > maxVerticalDeviation) return;
      if (deltaX > 0) return; // Only allow left swipes (negative deltaX)
      
      // Start swipe mode
      isSwipingRef.current = true;
      if (cardRef.current) {
        cardRef.current.style.transition = '';
      }
    }
    
    // Only update transform if we're in swipe mode
    if (isSwipingRef.current) {
      currentXRef.current = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      animationFrameRef.current = requestAnimationFrame(() => {
        updateTransform(deltaX);
      });
    }
  }, [updateTransform, showDelete]);

  const handleEnd = useCallback(() => {
    if (!isDraggingRef.current || !showDelete) return;
    
    isDraggingRef.current = false;
    const wasSwipping = isSwipingRef.current;
    isSwipingRef.current = false;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Only trigger delete if we were actually swiping and swiped far enough
    if (wasSwipping && currentXRef.current < -40 && onDelete) {
      onDelete();
    }
    
    resetPosition();
  }, [resetPosition, onDelete, showDelete]);

  // Mouse events
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleStart(e.clientX, e.clientY);
  }, [handleStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleMouseLeave = useCallback(() => {
    if (isDraggingRef.current) {
      handleEnd();
    }
  }, [handleEnd]);

  // Touch events
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleStart(touch.clientX, touch.clientY);
    }
  }, [handleStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleMove(touch.clientX, touch.clientY);
    }
  }, [handleMove]);

  const handleTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    if (isDraggingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onSelect();
  }, [onSelect]);

  return (
    <div 
      ref={(node) => {
        if (houseRef) houseRef(node);
      }}
      className="relative overflow-hidden rounded-lg"
    >
      {/* Delete Background */}
      {showDelete && (
        <div 
          ref={backgroundRef}
          className="absolute inset-0 bg-red-500 flex items-center justify-end pr-6 opacity-0"
        >
          <Trash2 className="h-6 w-6 text-white" />
        </div>
      )}
      
      {/* Main Card */}
      <Card 
        ref={cardRef}
        className={`p-4 cursor-pointer transition-colors relative z-10 ${
          isAreaInactive ? 'opacity-50 grayscale' :
          hasHotAndFriendly 
            ? 'ring-2 ring-green-500/20 bg-green-500/5 backdrop-blur-sm animate-border-travel-green animate-hot-pulse' 
            : hasHotLeads
            ? 'ring-2 ring-red-500/20 bg-red-500/5 backdrop-blur-sm animate-border-travel animate-hot-pulse'
            : hasMultipleKT 
            ? 'ring-2 ring-green-500/20 bg-green-500/5 backdrop-blur-sm animate-border-travel-green animate-hot-pulse' 
            : ''
        }`}
        onClick={handleCardClick}
        onMouseDown={showDelete ? handleMouseDown : undefined}
        onMouseMove={showDelete ? handleMouseMove : undefined}
        onMouseUp={showDelete ? handleMouseUp : undefined}
        onMouseLeave={showDelete ? handleMouseLeave : undefined}
        onTouchStart={showDelete ? handleTouchStart : undefined}
        onTouchMove={showDelete ? handleTouchMove : undefined}
        onTouchEnd={showDelete ? handleTouchEnd : undefined}
        style={{ 
          userSelect: 'none',
          touchAction: showDelete ? 'pan-y' : 'auto'
        }}
      >
        {/* Smiley for houses with multiple KT units */}
        {hasMultipleKT && (
          <span className="absolute bottom-2 right-2 text-lg font-bold text-green-600 z-20">☺</span>
        )}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">
                  {house.house_number}
                </h3>
                {usersInHouse.length > 0 && (
                  <PresenceIndicator users={usersInHouse} size="sm" />
                )}
                {house.units && house.units.length > 1 && (
                  <Badge variant="outline" className="text-xs">
                    {house.units.length} Einheiten
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-6 w-6 p-0 hover:bg-primary/10 ${house.notes && house.notes.length > 0 ? '' : 'opacity-50'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenHouseNotes?.();
                  }}
                >
                  <StickyNote className={`h-4 w-4 ${house.notes && house.notes.length > 0 ? 'text-primary' : 'text-muted-foreground'}`} />
                </Button>
              </div>
              
              {/* Show house notes directly */}
              {house.notes && house.notes.length > 0 && (
                <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded border-l-2 border-primary/50 mb-4">
                  {house.notes.map((note, idx) => (
                    <div key={note.id} className={cn(idx > 0 && "mt-1")}>
                      {note.note}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Show unit details when there's only one unit */}
            {house.units && house.units.length === 1 && (() => {
              const unit = house.units[0];
              
              return (
                <div className="space-y-2">
                  {/* Unit name */}
                  {unit.unit_name && (
                    <p className="text-sm text-muted-foreground mt-3">{unit.unit_name}</p>
                  )}
                  
                  {/* Last contact */}
                  {unit.last_contact_at && !(unit.tags as any)?.includes('WIN') && (
                    <p className="text-xs text-muted-foreground">
                      {(unit.tags as any)?.includes('NA') ? 'Letzter Versuch:' : 'Letzter Kontakt:'} {new Date(unit.last_contact_at).toLocaleDateString('de-DE')}
                    </p>
                  )}
                  
                   <div className="flex flex-wrap gap-1 items-center">
                     {/* Tags */}
                     {(unit.tags as any)?.includes('HOT') && (
                       <Badge variant="destructive" className="text-xs">🔥 HOT</Badge>
                     )}
                     {(unit.tags as any)?.includes('BK') && (
                       <Badge variant="default" className="text-xs bg-green-100 text-green-800">BK</Badge>
                     )}
                     {(unit.tags as any)?.includes('NA') && (
                       <Badge variant="secondary" className="text-xs">NA</Badge>
                     )}
                     {(unit.tags as any)?.includes('KT') && (
                       <Badge variant="default" className="text-xs bg-blue-100 text-blue-800">KT</Badge>
                     )}
                     {(unit.tags as any)?.includes('KI') && (
                       <Badge variant="outline" className="text-xs">KI</Badge>
                     )}
                     {(unit.tags as any)?.includes('NO') && (
                       <Badge variant="destructive" className="text-xs bg-red-100 text-red-800">NO</Badge>
                     )}
                     {(unit.tags as any)?.includes('WIN') && (
                       <Badge variant="default" className="text-xs bg-yellow-100 text-yellow-800">🏆 WIN</Badge>
                     )}
                     
                   </div>
                   
                   {/* Notes */}
                   {unit.notes && unit.notes.length > 0 && (showNotesGlobally || houseNotesVisibility[house.id]) && (
                     <div className="mt-2 space-y-1">
                       <p className="text-xs font-medium text-muted-foreground">Notizen:</p>
                       <div className="space-y-1">
                         {unit.notes.slice(0, 2).map((note) => (
                           <div key={note.id} className="bg-muted/20 rounded-md p-2 text-xs">
                             <p className="mb-1 line-clamp-2">{note.note}</p>
                             <p className="text-xs text-muted-foreground">
                               {new Date(note.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                             </p>
                           </div>
                         ))}
                         {unit.notes.length > 2 && (
                           <p className="text-xs text-muted-foreground italic">
                             +{unit.notes.length - 2} weitere Notizen
                           </p>
                         )}
                       </div>
                     </div>
                   )}

                  {/* Next reminder */}
                  {unit.next_reminder && !(unit.tags as any)?.includes('WIN') && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Follow-Up: {new Date(unit.next_reminder).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              );
            })()}
            
            {/* Show tag counts when there are multiple units */}
            {house.units && house.units.length > 1 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {/* HOT Tag */}
                {(() => {
                  const hotCount = house.units.filter(u => u.tags.includes('HOT')).length;
                  if (hotCount === 0) return null;
                  return (
                    <Badge 
                      variant="destructive" 
                      className="text-xs cursor-pointer hover:bg-red-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectWithTagFilter?.('HOT');
                      }}
                    >
                      🔥 HOT ({hotCount})
                    </Badge>
                  );
                })()}
                
                {/* BK Tag */}
                {(() => {
                  const bkCount = house.units.filter(u => u.tags.includes('BK')).length;
                  if (bkCount === 0) return null;
                  return (
                    <Badge 
                      variant="default" 
                      className="text-xs bg-green-100 text-green-800 cursor-pointer hover:bg-green-200 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectWithTagFilter?.('BK');
                      }}
                    >
                      BK ({bkCount})
                    </Badge>
                  );
                })()}
                
                {/* NA Tag */}
                {(() => {
                  const naCount = house.units.filter(u => u.tags.includes('NA')).length;
                  if (naCount === 0) return null;
                  return (
                    <Badge 
                      variant="secondary" 
                      className="text-xs cursor-pointer hover:bg-gray-300 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectWithTagFilter?.('NA');
                      }}
                    >
                      NA ({naCount})
                    </Badge>
                  );
                })()}
                
                {/* KT Tag */}
                {(() => {
                  const ktCount = house.units.filter(u => u.tags.includes('KT')).length;
                  if (ktCount === 0) return null;
                  return (
                    <Badge 
                      variant="default" 
                      className="text-xs bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectWithTagFilter?.('KT');
                      }}
                    >
                      KT ({ktCount})
                    </Badge>
                  );
                })()}
                
                {/* KI Tag */}
                {(() => {
                  const kiCount = house.units.filter(u => u.tags.includes('KI')).length;
                  if (kiCount === 0) return null;
                  return (
                    <Badge variant="outline" className="text-xs">
                      KI ({kiCount})
                    </Badge>
                  );
                })()}
                
                {/* WIN Tag */}
                {(() => {
                  const winCount = house.units.filter(u => u.tags.includes('WIN')).length;
                  if (winCount === 0) return null;
                  return (
                    <Badge 
                      variant="default" 
                      className="text-xs bg-yellow-100 text-yellow-800 cursor-pointer hover:bg-yellow-200 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectWithTagFilter?.('WIN');
                      }}
                    >
                      🏆 WIN ({winCount})
                    </Badge>
                  );
                })()}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2 ml-4">
            {/* Individual house notes toggle - only show if there are notes to toggle */}
            {house.units && house.units.some(unit => unit.notes && unit.notes.length > 0) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleHouseNotes(house.id);
                }}
                className="p-1 h-6 w-6 hover:bg-muted/50"
              >
                {(showNotesGlobally || houseNotesVisibility[house.id]) ? 
                  <Eye className="h-3 w-3 text-muted-foreground" /> : 
                  <EyeOff className="h-3 w-3 text-muted-foreground" />
                }
              </Button>
            )}
            {(() => {
              const unitCount = house.units?.length || 0;
              if (unitCount >= 10) return <Building2 className="h-4 w-4 text-muted-foreground" />;
              if (unitCount >= 2) return <Building2 className="h-4 w-4 text-muted-foreground" />;
              return <Home className="h-4 w-4 text-muted-foreground" />;
            })()}
          </div>
        </div>
      </Card>
    </div>
  );
};

export const D2DHouseList = ({ 
  houses, 
  loading, 
  street,
  onSelectHouse,
  onSelectHouseWithTagFilter, 
  onCreateHouse,
  onCreateUnit,
  onDeleteHouse,
  onBack,
  onAddHouseNote,
  onUpdateHouseNote,
  onDeleteHouseNote,
  isAreaInactive = false,
  getUsersInHouse
}: D2DHouseListProps) => {
  const { toast } = useToast();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const houseRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [houseNumber, setHouseNumber] = useState('');
  const [unitName, setUnitName] = useState('');
  const [selectedTags, setSelectedTags] = useState<D2DTag[]>([]);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [ktFollowUpData, setKTFollowUpData] = useState<{ date: Date; notes?: string } | null>(null);
  const [lastCreatedHouseNumber, setLastCreatedHouseNumber] = useState<string | null>(null);
  const [sortAscending, setSortAscending] = useState(() => {
    try {
      // First try to restore from session storage
      const saved = sessionStorage.getItem(HOUSE_LIST_STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.sortAscending !== undefined) return parsed.sortAscending;
      }
      // Fallback to localStorage for backward compatibility
      if (!street) return true;
      const key = `d2d-sort-${street.name}-${street.plz}`;
      const localSaved = localStorage.getItem(key);
      return localSaved ? JSON.parse(localSaved) : true;
    } catch (error) {
      console.error('Error loading sort state:', error);
      return true;
    }
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [houseToDelete, setHouseToDelete] = useState<D2DHouse | null>(null);
  const [showNotesGlobally, setShowNotesGlobally] = useState(() => {
    try {
      const saved = sessionStorage.getItem(HOUSE_LIST_STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.showNotesGlobally !== undefined) return parsed.showNotesGlobally;
      }
    } catch (error) {
      console.error('Error loading notes visibility state:', error);
    }
    return false;
  });
  const [houseNotesVisibility, setHouseNotesVisibility] = useState<{ [houseId: string]: boolean }>({});
  const [showBulkCreator, setShowBulkCreator] = useState(false);
  const [bulkUnits, setBulkUnits] = useState<Array<{ name: string; notes?: string }>>([]);
  const [showHouseNotesDialog, setShowHouseNotesDialog] = useState(false);
  const [selectedHouseForNotes, setSelectedHouseForNotes] = useState<D2DHouse | null>(null);
  const [houseNoteText, setHouseNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);

  // Update selectedHouseForNotes when houses data changes
  useEffect(() => {
    if (selectedHouseForNotes && houses.length > 0) {
      const updatedHouse = houses.find(h => h.id === selectedHouseForNotes.id);
      if (updatedHouse) {
        setSelectedHouseForNotes(updatedHouse);
      }
    }
  }, [houses]);

  // Save state whenever it changes
  useEffect(() => {
    const state = {
      sortAscending,
      showNotesGlobally
    };
    sessionStorage.setItem(HOUSE_LIST_STATE_KEY, JSON.stringify(state));
  }, [sortAscending, showNotesGlobally]);

  // Save and restore scroll position
  useEffect(() => {
    const saveScrollPosition = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        sessionStorage.setItem(HOUSE_LIST_SCROLL_KEY, scrollTop.toString());
      }
    };

    const handleBeforeUnload = () => saveScrollPosition();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveScrollPosition();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      saveScrollPosition(); // Save on unmount
    };
  }, []);

  // Restore scroll position after content loads
  useEffect(() => {
    if (houses.length > 0) {
      setTimeout(() => {
        const savedScrollPosition = sessionStorage.getItem(HOUSE_LIST_SCROLL_KEY);
        if (savedScrollPosition && scrollContainerRef.current) {
          const scrollTop = parseInt(savedScrollPosition, 10);
          scrollContainerRef.current.scrollTop = scrollTop;
          console.log('🔄 House List: Restored scroll position:', scrollTop);
        }
      }, 100);
    }
  }, [houses.length]);

  // Scroll to newly created house and navigate into it
  useEffect(() => {
    if (lastCreatedHouseNumber && houses.length > 0) {
      const newHouse = houses.find(h => h.house_number === lastCreatedHouseNumber);
      if (newHouse) {
        // Navigate into the house immediately
        setTimeout(() => {
          onSelectHouse(newHouse);
          setLastCreatedHouseNumber(null); // Reset after navigating
        }, 300);
      }
    }
  }, [lastCreatedHouseNumber, houses, onSelectHouse]);

  // Sort houses by house number
  const sortHousesByNumber = (housesToSort: D2DHouse[]) => {
    return [...housesToSort].sort((a, b) => {
      // Extract numeric part and letters from house numbers for proper sorting
      const parseHouseNumber = (houseNum: string) => {
        const match = houseNum.match(/^(\d+)([a-zA-Z]*)(.*)$/);
        if (match) {
          return {
            number: parseInt(match[1], 10),
            letter: match[2] || '',
            suffix: match[3] || ''
          };
        }
        return { number: 0, letter: houseNum, suffix: '' };
      };

      const aNum = parseHouseNumber(a.house_number);
      const bNum = parseHouseNumber(b.house_number);

      // Compare numbers first
      if (aNum.number !== bNum.number) {
        return sortAscending ? aNum.number - bNum.number : bNum.number - aNum.number;
      }

      // If numbers are equal, compare letters
      if (aNum.letter !== bNum.letter) {
        return sortAscending 
          ? aNum.letter.localeCompare(bNum.letter)
          : bNum.letter.localeCompare(aNum.letter);
      }

      // Finally compare suffix
      return sortAscending 
        ? aNum.suffix.localeCompare(bNum.suffix)
        : bNum.suffix.localeCompare(aNum.suffix);
    });
  };

  const sortedHouses = sortHousesByNumber(houses);

  // Save sort order to localStorage whenever it changes
  useEffect(() => {
    if (!street) return;
    const key = `d2d-sort-${street.name}-${street.plz}`;
    localStorage.setItem(key, JSON.stringify(sortAscending));
  }, [sortAscending, street]);

  // Toggle functions for notes visibility
  const toggleGlobalNotesVisibility = () => {
    setShowNotesGlobally(prev => !prev);
  };

  const toggleHouseNotesVisibility = (houseId: string) => {
    setHouseNotesVisibility(prev => ({
      ...prev,
      [houseId]: !prev[houseId]
    }));
  };

  const resetCreateForm = () => {
    setHouseNumber('');
    setUnitName('');
    setSelectedTags([]);
    setNote('');
    setKTFollowUpData(null);
    setBulkUnits([]);
  };

  const handleCreate = async () => {
    if (!houseNumber.trim()) return;
    
    // Check if house number already exists for this street
    const existingHouse = houses.find(house => 
      house.house_number.toLowerCase() === houseNumber.trim().toLowerCase()
    );
    
    if (existingHouse) {
      toast({
        title: "Fehler",
        description: `Hausnummer "${houseNumber.trim()}" existiert bereits für diese Straße.`,
        variant: "destructive",
      });
      return;
    }
    
    const createdHouseNumber = houseNumber.trim();
    setCreating(true);
    try {
      // Check if we have bulk units or regular single unit
      const hasMultipleUnits = bulkUnits.length > 0;
      const firstUnit = hasMultipleUnits 
        ? { 
            unit_name: bulkUnits[0].name,
            tags: selectedTags,
            note: bulkUnits[0].notes || note.trim() || undefined,
            reminder: ktFollowUpData ? JSON.stringify({
              date: ktFollowUpData.date.toISOString(),
              notes: ktFollowUpData.notes
            }) : undefined,
          }
        : {
            unit_name: unitName.trim() || undefined,
            tags: selectedTags,
            note: note.trim() || undefined,
            reminder: ktFollowUpData ? JSON.stringify({
              date: ktFollowUpData.date.toISOString(),
              notes: ktFollowUpData.notes
            }) : undefined,
          };
      
      const newHouse = await onCreateHouse({
        house_number: createdHouseNumber,
        first_unit: firstUnit
      });
      
      // If we have additional bulk units, add them now
      if (newHouse && hasMultipleUnits && bulkUnits.length > 1 && onCreateUnit) {
        // Add remaining units
        for (let i = 1; i < bulkUnits.length; i++) {
          const unit = bulkUnits[i];
          await onCreateUnit(newHouse.id, {
            unit_name: unit.name,
            tags: [],
            note: unit.notes || undefined
          });
        }
      }
      
      // Set the house number to trigger navigation effect
      setLastCreatedHouseNumber(createdHouseNumber);
      
      // Reset form
      setHouseNumber('');
      setUnitName('');
      setSelectedTags([]);
      setNote('');
      setKTFollowUpData(null);
      setBulkUnits([]);
      setShowCreateDialog(false);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteHouse = async () => {
    if (!houseToDelete || !onDeleteHouse) return;
    
    try {
      await onDeleteHouse(houseToDelete.id);
      toast({
        title: "Erfolg",
        description: `Hausnummer ${houseToDelete.house_number} wurde gelöscht.`,
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Hausnummer konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setHouseToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-6 bg-muted rounded w-32 animate-pulse"></div>
        </div>
        {[1, 2, 3].map(i => (
          <Card key={i} className="p-6 animate-pulse">
            <div className="h-6 bg-muted rounded w-1/2 mb-3"></div>
            <div className="h-4 bg-muted rounded w-1/4"></div>
          </Card>
        ))}
      </div>
    );
  }

  const openMapsForStreet = () => {
    if (!street) return;
    
    openNavigationForStreet(street.name, street.plz);
  };

  return (
    <div ref={scrollContainerRef} className="space-y-4">
      {street && (
        <div className="flex justify-center mb-4">
          <div 
            className="flex flex-col items-center justify-center gap-1 py-4 px-8 cursor-pointer bg-gradient-to-br from-primary/10 via-background to-accent/10 hover:from-primary/15 hover:via-background hover:to-accent/15 rounded-2xl transition-all border-2 border-primary/20 hover:border-primary/40 shadow-lg backdrop-blur-sm hover:shadow-xl group"
            onClick={openMapsForStreet}
          >
            <h2 className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent text-center">
              {street.name}
            </h2>
            <p className="text-sm font-medium text-muted-foreground">{street.plz}</p>
            <MapIcon className="h-5 w-5 text-primary group-hover:scale-110 transition-transform mt-1" />
          </div>
        </div>
      )}
      
      <div className="flex items-start justify-between gap-2 mb-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack} className="hover:bg-transparent border border-border rounded-sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Home className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Hausnummern</h2>
          </div>
          <div className="ml-16 flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSortAscending(!sortAscending)}
              className="gap-2 text-muted-foreground hover:text-muted-foreground hover:bg-transparent"
              title={sortAscending ? "Absteigend sortieren" : "Aufsteigend sortieren"}
            >
              <ArrowUpDown className="h-4 w-4" />
              {sortAscending ? "1→9" : "9→1"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleGlobalNotesVisibility}
              className="gap-2 text-muted-foreground hover:text-muted-foreground hover:bg-transparent"
            >
              {showNotesGlobally ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              Notizen
            </Button>
          </div>
        </div>
      </div>
      
      {/* Add House Button */}
      <div className="flex flex-col items-center gap-1 mb-4">
        {isAreaInactive ? (
          <>
            <Button 
              variant="ghost" 
              size="sm" 
              disabled
              className="gap-2 text-muted-foreground font-medium bg-muted/10 backdrop-blur-sm border-2 border-border/50 rounded-full px-5 py-2 opacity-50 cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />
              Hausnummer hinzufügen
            </Button>
            <span className="text-xs text-muted-foreground">Gebiet ist inaktiv</span>
          </>
        ) : (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowCreateDialog(true)}
            className="gap-2 text-foreground font-medium hover:bg-muted/10 backdrop-blur-sm border-2 border-border rounded-full px-5 py-2 transition-all duration-200"
          >
            <Plus className="h-4 w-4" />
            Hausnummer hinzufügen
          </Button>
        )}
      </div>
      
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        // Prevent closing while we are creating to avoid "ghost" states
        if (!open && creating) return;

        setShowCreateDialog(open);
        if (!open) {
          resetCreateForm();
        }
      }}>
        <DialogContent
          className="max-w-md"
          aria-busy={creating}
          onEscapeKeyDown={(e) => {
            if (creating) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (creating) e.preventDefault();
          }}
        >
          {creating && (
            <div className="absolute inset-0 z-50 grid place-items-center rounded-lg bg-background/70 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2 text-sm text-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Wird angelegt…</span>
              </div>
            </div>
          )}

          <DialogHeader>
            <DialogTitle>Neue Hausnummer anlegen</DialogTitle>
          </DialogHeader>
          <div className={cn("space-y-4", creating && "opacity-60")}
          >
            <div>
              <Label htmlFor="house-number">Hausnummer *</Label>
              <Input
                id="house-number"
                value={houseNumber}
                onChange={(e) => setHouseNumber(e.target.value)}
                placeholder="z.B. 15, 15a, 15-17"
                disabled={creating}
              />
            </div>
            
            <div>
              <Label htmlFor="unit-name">Wohneinheit Name (optional)</Label>
              <div className="flex">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowBulkCreator(true)}
                  className="shrink-0 rounded-r-none border-r-0 hover:bg-accent"
                  disabled={bulkUnits.length > 1 || creating}
                >
                  <ScanLine className="h-4 w-4" />
                </Button>
                <Input
                  id="unit-name"
                  value={unitName}
                  onChange={(e) => setUnitName(e.target.value)}
                  placeholder="z.B. Familie Müller, EG links"
                  className="flex-1 rounded-l-none border-l-0 focus-visible:border-l"
                  disabled={bulkUnits.length > 1 || creating}
                />
              </div>
            </div>
            
            {/* Show recognized units from bulk creator */}
            {bulkUnits.length > 1 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Erkannte Wohneinheiten</Label>
                  <Badge variant="secondary">{bulkUnits.length} Einheiten</Badge>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-2 p-3 bg-muted/30 rounded-lg border">
                  {bulkUnits.map((unit, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm">
                      <span className="text-muted-foreground min-w-[20px]">{index + 1}.</span>
                      <div className="flex-1">
                        <p className="font-medium">{unit.name}</p>
                        {unit.notes && (
                          <p className="text-xs text-muted-foreground mt-1">{unit.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <Label>Tags für erste Wohneinheit (optional)</Label>
              <div className={cn(
                "mt-2",
                (bulkUnits.length > 1 || creating) && "pointer-events-none opacity-50"
              )}>
                <D2DTagSelector
                  selectedTags={selectedTags}
                  onTagsChange={setSelectedTags}
                  onKTFollowUp={(date, notes, isManual, customerEmail) => {
                    setKTFollowUpData({ date, notes });
                  }}
                  disableKTTag={true}
                  disabled={isAreaInactive || creating}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="note">Notiz (optional)</Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Notizen zum ersten Kontakt..."
                rows={3}
                disabled={bulkUnits.length > 1 || creating}
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  resetCreateForm();
                  setShowCreateDialog(false);
                }}
                className="flex-1"
                disabled={creating}
              >
                Abbrechen
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={!houseNumber.trim() || creating}
                className="flex-1"
              >
                {creating ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Erstelle...
                  </span>
                ) : (
                  'Erstellen'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BulkUnitCreator
        open={showBulkCreator}
        onClose={() => setShowBulkCreator(false)}
        onCreateUnits={async (units) => {
          if (units.length > 0) {
            // Store all scanned units (but don't set unitName)
            setBulkUnits(units);
            toast({
              title: `${units.length} Wohneinheit${units.length > 1 ? 'en' : ''} erkannt`,
              description: `${units.length} Einheit${units.length > 1 ? 'en' : ''} wird/werden zur Hausnummer hinzugefügt.`,
            });
          }
          setShowBulkCreator(false);
        }}
      />

      <div className="space-y-3">
         {sortedHouses.map((house) => (
            <SwipeableHouseCard
              key={house.id}
              house={house}
              onSelect={() => onSelectHouse(house)}
              onSelectWithTagFilter={(tagFilter) => onSelectHouseWithTagFilter?.(house, tagFilter)}
              onDelete={onDeleteHouse ? () => {
                setHouseToDelete(house);
                setDeleteDialogOpen(true);
              } : undefined}
              showDelete={!!onDeleteHouse}
              showNotesGlobally={showNotesGlobally}
              houseNotesVisibility={houseNotesVisibility}
              onToggleHouseNotes={toggleHouseNotesVisibility}
              onOpenHouseNotes={() => {
                setSelectedHouseForNotes(house);
                setShowHouseNotesDialog(true);
                setHouseNoteText('');
                setEditingNoteId(null);
              }}
              houseRef={(node) => {
                if (node) {
                  houseRefs.current.set(house.id, node);
                } else {
                  houseRefs.current.delete(house.id);
                }
              }}
              isAreaInactive={isAreaInactive}
              usersInHouse={getUsersInHouse?.(house.id)}
            />
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hausnummer löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie die Hausnummer "{houseToDelete?.house_number}" und alle dazugehörigen 
              {houseToDelete?.units && houseToDelete.units.length === 1 ? ' Wohneinheit' : ` ${houseToDelete?.units?.length || 0} Wohneinheiten`} 
              löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteHouse}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* House Notes Dialog */}
      <Dialog open={showHouseNotesDialog} onOpenChange={(open) => {
        setShowHouseNotesDialog(open);
        if (!open) {
          setSelectedHouseForNotes(null);
          setHouseNoteText('');
          setEditingNoteId(null);
        }
      }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Hausnummer {selectedHouseForNotes?.house_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Existing Notes */}
            {selectedHouseForNotes?.notes && selectedHouseForNotes.notes.length > 0 && (
              <div className="space-y-2">
                <Label>Vorhandene Notizen</Label>
                <div className="space-y-2">
                  {selectedHouseForNotes.notes.map((note) => (
                    <div key={note.id} className="bg-muted/20 rounded-md p-3">
                      {editingNoteId === note.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={houseNoteText}
                            onChange={(e) => setHouseNoteText(e.target.value)}
                            placeholder="Notiz bearbeiten..."
                            className="min-h-[60px]"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={async () => {
                                if (onUpdateHouseNote && houseNoteText.trim() && selectedHouseForNotes) {
                                  await onUpdateHouseNote(note.id, houseNoteText);
                                  const houseId = selectedHouseForNotes.id;
                                  setEditingNoteId(null);
                                  setHouseNoteText('');
                                  setShowHouseNotesDialog(false);
                                  setSelectedHouseForNotes(null);
                                  
                                  // Scroll to house after closing dialog
                                  setTimeout(() => {
                                    const houseElement = houseRefs.current.get(houseId);
                                    if (houseElement) {
                                      houseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                  }, 100);
                                }
                              }}
                            >
                              Speichern
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingNoteId(null);
                                setHouseNoteText('');
                              }}
                            >
                              Abbrechen
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm mb-2">{note.note}</p>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                              {new Date(note.created_at).toLocaleDateString('de-DE', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric',
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </p>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  setEditingNoteId(note.id);
                                  setHouseNoteText(note.note);
                                }}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                onClick={async () => {
                                  if (onDeleteHouseNote && selectedHouseForNotes) {
                                    const houseId = selectedHouseForNotes.id;
                                    const isLastNote = selectedHouseForNotes.notes?.length === 1;
                                    
                                    await onDeleteHouseNote(selectedHouseForNotes.id, note.id);
                                    
                                    // Only close the dialog if it was the last note
                                    if (isLastNote) {
                                      setShowHouseNotesDialog(false);
                                      setSelectedHouseForNotes(null);
                                      
                                      // Scroll to the house number after a short delay
                                      setTimeout(() => {
                                        const houseElement = houseRefs.current.get(houseId);
                                        if (houseElement) {
                                          houseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        }
                                      }, 100);
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Add New Note */}
            {editingNoteId === null && (
              <div className="space-y-2">
                <Label>Neue Notiz hinzufügen</Label>
                <Textarea
                  value={houseNoteText}
                  onChange={(e) => setHouseNoteText(e.target.value)}
                  placeholder="Notiz für diese Hausnummer..."
                  className="min-h-[80px]"
                />
                <Button
                  onClick={async () => {
                    if (onAddHouseNote && selectedHouseForNotes && houseNoteText.trim()) {
                      await onAddHouseNote(selectedHouseForNotes.id, houseNoteText);
                      setHouseNoteText('');
                      setShowHouseNotesDialog(false); // Close dialog after adding note
                      setSelectedHouseForNotes(null);
                    }
                  }}
                  disabled={!houseNoteText.trim()}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Notiz hinzufügen
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add House Button */}
      {houses.length > 3 && (
        <div className="flex justify-center pt-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowCreateDialog(true)}
            className="gap-2 border-2 border-border font-medium text-foreground hover:bg-muted/10 backdrop-blur-sm rounded-full px-5 py-2 transition-all duration-200"
          >
            <Plus className="h-4 w-4" />
            Neue Hausnummer hinzufügen
          </Button>
        </div>
      )}
    </div>
  );
};